// @ts-strict-ignore
import * as React from 'react';
import { antGrey, releaseSettingsDescriptionsStyle, runwayColors } from './styles/globalStyles';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import Paragraph from 'antd/lib/typography/Paragraph';
import { ReviewAttachment, ReviewAttachmentUploadStatus } from '../types';
import { formatBytes } from '../helpers';
import { iconForFileType } from '../helpers/fileDisplayHelpers';
import Tooltip from 'antd/lib/tooltip';
import { EditAppleReviewInfoModal } from '@/components/EditAppleReviewInfoModal';
import { Nullable } from '@/typescriptHelpers';
import { StepRunComponentProps } from '@/typescriptHelpers';

interface AppleReviewInfoProps {
    storeUrl: string;
    contactFirstName?: string;
    contactLastName?: string;
    contactEmail?: string;
    contactPhone?: string;
    notes?: string;
    demoAccountName?: string;
    id: string;
    demoAccountPassword?: string;
    demoAccountRequired?: boolean;
    reviewAttachments?: ReviewAttachment[];
    isDefaultCollapsed: boolean;
    releaseId: string;
    appId: string;
    orgId: string;
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const AppleReviewInfo: React.FC<AppleReviewInfoProps> = (props: AppleReviewInfoProps) => {
    const signInRequiredText =
        props.demoAccountRequired !== null ?
            props.demoAccountRequired ?
                'Yes'
            :   'No'
        :   'Not set';
    const contactName =
        props.contactFirstName && props.contactLastName ?
            `${props.contactFirstName} ${props.contactLastName}`
        :   (props.contactFirstName ?? props.contactLastName);

    const hasContactInfo = contactName || props.contactEmail || props.contactPhone;

    const hasCompletedFileUpload = props.reviewAttachments?.some(
        (attachment) => attachment.status === ReviewAttachmentUploadStatus.Complete,
    );

    const hasFailedFileUpload = props.reviewAttachments?.some(
        (attachment) => attachment.status === ReviewAttachmentUploadStatus.Failed,
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Descriptions
                colon={false}
                column={1}
                labelStyle={releaseSettingsDescriptionsStyle}
                contentStyle={{ color: antGrey.grey7, paddingBottom: 12 }}
                style={{ paddingLeft: 12 }}
            >
                <Descriptions.Item label="Contact">
                    {hasContactInfo ?
                        <Paragraph style={{ color: antGrey.grey7, marginBottom: 0 }}>
                            {contactName}
                            <br />
                            {props.contactEmail}
                            <br />
                            {props.contactPhone}
                        </Paragraph>
                    :   'No contact info'}
                </Descriptions.Item>
                <Descriptions.Item label="Sign-in required">{signInRequiredText}</Descriptions.Item>
                {props.demoAccountName && props.demoAccountPassword && (
                    <Descriptions.Item label="Sign-in credentials">
                        <Paragraph style={{ color: antGrey.grey7, marginBottom: 0 }}>
                            Username: {props.demoAccountName}
                            <br />
                            Password: {props.demoAccountPassword}
                        </Paragraph>
                    </Descriptions.Item>
                )}
                {props.notes && <Descriptions.Item label="Review notes">{props.notes}</Descriptions.Item>}
                {(hasCompletedFileUpload || hasFailedFileUpload) && (
                    <Descriptions.Item label="Attachments">
                        {props.reviewAttachments.map((attachment) => (
                            <Paragraph key={attachment.fileName}>
                                {attachment.status === ReviewAttachmentUploadStatus.Failed ?
                                    <Tooltip
                                        title={
                                            'Your default review attachment failed to upload. Please upload your attachment directly in App Store Connect.'
                                        }
                                    >
                                        <ExclamationCircleOutlined
                                            style={{ color: runwayColors.statusRed, padding: 4 }}
                                        />
                                    </Tooltip>
                                :   <Text>{iconForFileType(attachment.fileName)}</Text>}{' '}
                                {attachment.status === ReviewAttachmentUploadStatus.Failed ||
                                    (attachment.status === ReviewAttachmentUploadStatus.Complete && (
                                        <Text style={{ color: antGrey.grey7, marginBottom: 0 }}>
                                            {attachment.fileName} ({formatBytes(attachment.fileSize)})
                                        </Text>
                                    ))}
                            </Paragraph>
                        ))}
                    </Descriptions.Item>
                )}
            </Descriptions>
            <EditAppleReviewInfoModal
                stepRunComponentProps={props.stepRunComponentProps}
                open={props.isModalOpen}
                releaseId={props.releaseId}
                appId={props.appId}
                orgId={props.orgId}
                reviewId={props.id}
                onCancel={() => props.setIsModalOpen(false)}
                initialValues={{
                    contactFirstName: props.contactFirstName ?? '',
                    contactLastName: props.contactLastName ?? '',
                    contactEmail: props.contactEmail ?? '',
                    contactPhone: props.contactPhone ?? '',
                    notes: props.notes ?? '',
                    demoAccountRequired: props.demoAccountRequired,
                    demoAccountName: props.demoAccountName,
                    demoAccountPassword: props.demoAccountPassword,
                }}
            />
        </div>
    );
};

export default AppleReviewInfo;
